import { useCallback, useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import {
  fetchSession,
  type ConversationRow,
  type EmailThreadEntry,
  type MessageRow,
  type PlanRow,
  type SessionDetailResponse,
  type TdeeResultRow,
} from './api'
import { PlanCtaCard } from '../components/PlanCtaCard'
import { isPhoneLikeUserAgent } from '../components/planCtaSurface'
import type { CalcOutput } from '../lib/types'

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleString()
}

// plans.model_provider → human label. 'plan-card-render' rows were created
// by the initial-plan email path embedding the EC2-rendered plan-card image
// (the markdown is the card's PLAN.md content, not an LLM chat plan), so the
// timeline says where the plan came from instead of showing a bare slug.
function providerLabel(provider: string): string {
  if (provider === 'plan-card-render') return 'plan-card render (email)'
  return provider
}

function fmtRange(low: number, high: number): string {
  return low === high ? `${low}` : `${low}–${high}`
}

function ResultCard({ r }: { r: TdeeResultRow }) {
  const bf = r.body_fat_pct != null ? `, ${r.body_fat_pct}% BF` : ''
  return (
    <div className="result-card">
      <div className="result-head">
        <div className="result-title">
          TDEE {fmtRange(r.tdee_low, r.tdee_high)} kcal · rec {r.tdee_recommended}
        </div>
        <div className="muted small tabular">{fmtTime(r.created_at)}</div>
      </div>
      <div className="result-bio muted small">
        {r.sex}, {r.age_years}y, {r.height_cm}cm, {r.weight_kg}kg{bf} · {r.occupation} ·{' '}
        {r.diet_style} · {r.exercise_count} exercise(s) · {r.bmr_method}
      </div>
      <table className="data compact result-breakdown">
        <tbody>
          <tr>
            <td>BMR</td>
            <td className="num tabular">{r.bmr_kcal}</td>
          </tr>
          <tr>
            <td>NEAT</td>
            <td className="num tabular">{fmtRange(r.neat_low, r.neat_high)}</td>
          </tr>
          <tr>
            <td>TEF</td>
            <td className="num tabular">{fmtRange(r.tef_low, r.tef_high)}</td>
          </tr>
          <tr>
            <td>EAT</td>
            <td className="num tabular">{fmtRange(r.eat_low, r.eat_high)}</td>
          </tr>
          <tr>
            <td>Traditional ({r.traditional_bucket})</td>
            <td className="num tabular">{r.traditional_tdee}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// Reconstructs the exact PlanCtaCard the user saw. The CTA is a client-side
// render concept (never persisted), so it is re-derived the same way App.tsx
// derives it: from the newest calc result's output_json + the conversation
// row, through the REAL component — copy drift is impossible. The stage is
// pointer-events-none so admin clicks can never fire the card's click
// beacons or ads conversions (they'd stamp the USER's funnel columns).
// Device defaults to what the conversation's user_agent implies — the action
// stack differs (mobile = SMS button + WA link, desktop = WhatsApp QR) — and
// the toggle shows the other variant.
function CtaReplay({ c, result }: { c: ConversationRow; result: TdeeResultRow }) {
  const uaPhone = isPhoneLikeUserAgent(c.user_agent ?? '', 0)
  const [device, setDevice] = useState<'phone' | 'desktop'>(uaPhone ? 'phone' : 'desktop')
  let payload: CalcOutput | null = null
  try {
    payload = JSON.parse(result.output_json) as CalcOutput
  } catch {
    payload = null
  }
  if (!payload) {
    return <div className="muted small">CTA replay unavailable — output_json unparsable.</div>
  }
  const token = payload.handoff_token ?? c.handoff_token
  if (payload.is_minor) {
    return <div className="muted small">No CTA — minor-blocked session.</div>
  }
  if (!token) {
    return <div className="muted small">No CTA — session has no handoff token.</div>
  }
  const lang = c.language === 'es' ? ('es' as const) : ('en' as const)
  return (
    <div className="admin-cta-replay">
      <div className="admin-cta-replay-head">
        <span className="muted small">
          As the user saw it · {payload.country ?? c.country ?? 'country?'} · {lang}
        </span>
        <span className="admin-cta-replay-toggle">
          <button
            type="button"
            className={device === 'phone' ? 'on' : ''}
            onClick={() => setDevice('phone')}
          >
            📱 phone{uaPhone ? ' · their device' : ''}
          </button>
          <button
            type="button"
            className={device === 'desktop' ? 'on' : ''}
            onClick={() => setDevice('desktop')}
          >
            🖥️ desktop{!uaPhone ? ' · their device' : ''}
          </button>
        </span>
      </div>
      <div className={`admin-cta-replay-stage ${device}`}>
        <PlanCtaCard
          conversationId={null}
          handoffToken={token}
          country={payload.country ?? c.country}
          tdeeRecommended={payload.tdee_recommended ?? null}
          tdeeLow={payload.tdee_low ?? null}
          tdeeHigh={payload.tdee_high ?? null}
          isMinor={payload.is_minor}
          hasExistingHandoff={payload.has_existing_handoff}
          isGlp1={payload.on_glp1}
          intent={payload.intent}
          honestPlan={payload.honest_plan ?? null}
          lang={lang}
          forceDevice={device}
        />
      </div>
    </div>
  )
}

export function SessionDetail({
  token,
  id,
  onBack,
}: {
  token: string
  id: string
  onBack: () => void
}) {
  const [data, setData] = useState<SessionDetailResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await fetchSession(token, id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed')
    } finally {
      setLoading(false)
    }
  }, [token, id])

  useEffect(() => {
    void load()
  }, [load])

  if (loading && !data) return <div className="muted">Loading session…</div>
  if (error) return <div className="admin-error">Error: {error}</div>
  if (!data) return null

  const { conversation: c, messages, results, plans, emails } = data

  // Split plans by where the user actually saw them. In-chat plans are rows
  // written by the /api/plan stream (provider 'anthropic'/'openai', or null
  // on legacy rows). '-backfill' rows were generated server-side as the
  // initial-plan EMAIL body (emails.ts ensurePlanMarkdown) — the user never
  // saw them in the chat, and since the 2026-06-10 US funnel change US
  // sessions NEVER stream a web plan, so an inlined backfill card reads like
  // a regression. Backfill plans render in the behavior timeline instead.
  const allPlans = plans ?? []
  const chatPlans = allPlans.filter((p) => !isBackfillPlan(p))
  const emailPlans = allPlans.filter((p) => isBackfillPlan(p))

  // Transcript = exactly what the user saw in the chat: real message turns,
  // TDEE result cards, in-chat streamed plan cards, and the reconstructed
  // CTA card (newest calc feeds it, same as App.tsx's append logic).
  const lastResult = results.length > 0 ? results[results.length - 1] : null
  const ctaSlot = lastResult
    ? { at: c.cta_shown_at ?? lastResult.created_at, row: lastResult }
    : null
  const timeline = buildTimeline(messages, results, chatPlans, ctaSlot)

  return (
    <div className="session-detail">
      <div className="detail-head">
        <button className="ghost" onClick={onBack}>
          ← Back
        </button>
        <div className="detail-meta">
          <div className="detail-id tabular">{c.id}</div>
          <div className="muted small">
            Created {fmtTime(c.created_at)} · Updated {fmtTime(c.updated_at)} ·{' '}
            {c.country ?? 'unknown'} · strategy <code>{c.strategy}</code> ·{' '}
            {c.message_count} msgs · {c.calc_count} calcs ·{' '}
            {results.length} result{results.length === 1 ? '' : 's'} ·{' '}
            {chatPlans.length} chat plan{chatPlans.length === 1 ? '' : 's'}
            {emailPlans.length > 0 && (
              <> · {emailPlans.length} email-only plan{emailPlans.length === 1 ? '' : 's'}</>
            )}
          </div>
          {c.user_agent && (
            <div className="muted small detail-ua" title={c.user_agent}>
              {c.user_agent}
            </div>
          )}
        </div>
      </div>

      <section className="panel">
        <div className="panel-head">
          <h2>Transcript ({messages.length})</h2>
        </div>
        <div className="transcript">
          {timeline.length === 0 && <div className="muted">No messages.</div>}
          {timeline.map((item) => {
            if (item.kind === 'message') {
              const m = item.msg
              return (
                <div key={`m-${m.id}`} className={`msg msg-${m.role}`}>
                  <div className="msg-meta muted small">
                    {m.role} · turn {m.turn_index} · {fmtTime(m.created_at)}
                  </div>
                  <div className="msg-body">
                    {m.role === 'assistant' ? (
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    ) : (
                      <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{m.content}</p>
                    )}
                  </div>
                </div>
              )
            }
            if (item.kind === 'result') {
              return (
                <div key={`r-${item.row.id}`} className="msg msg-assistant">
                  <div className="msg-meta muted small">
                    TDEE result · {fmtTime(item.row.created_at)}
                  </div>
                  <ResultCard r={item.row} />
                </div>
              )
            }
            if (item.kind === 'cta') {
              return (
                <div key="cta-replay" className="msg msg-assistant">
                  <div className="msg-meta muted small">
                    CTA card{' '}
                    {c.cta_shown_at ? `· ${fmtTime(c.cta_shown_at)}` : '· reconstructed'}
                  </div>
                  <CtaReplay c={c} result={item.row} />
                </div>
              )
            }
            // plan — only in-chat streamed plans reach the transcript
            return (
              <div key={`p-${item.row.kind}-${item.row.generated_at}`} className="msg msg-assistant">
                <div className="msg-meta muted small">
                  {item.row.kind === 'plan' ? 'Personalized plan' : 'Sample meal day'} ·{' '}
                  {fmtTime(item.row.generated_at)}
                  {item.row.model_provider && <> · {providerLabel(item.row.model_provider)}</>}
                </div>
                <div className="msg-body plan-body">
                  <ReactMarkdown>{item.row.markdown_body}</ReactMarkdown>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <BehaviorTimeline c={c} results={results} plans={allPlans} emails={emails ?? []} />

      {emails && emails.length > 0 && (
        <section className="panel">
          <div className="panel-head">
            <h2>Email thread ({emails.length})</h2>
          </div>
          <EmailThread entries={emails} />
        </section>
      )}
    </div>
  )
}

// Email-deliverable plan rows — the markdown never appeared in the chat UI:
//   - '-backfill' providers: emails.ts ensurePlanMarkdown wrote the row as
//     the initial-plan email body (LLM-generated server-side)
//   - 'plan-card-render': the initial-plan email embedded the EC2-rendered
//     plan-card image; the row holds the card's PLAN.md markdown
function isBackfillPlan(p: PlanRow): boolean {
  const provider = p.model_provider ?? ''
  return provider.includes('backfill') || provider === 'plan-card-render'
}

type TimelineItem =
  | { kind: 'message'; at: number; msg: MessageRow }
  | { kind: 'result'; at: number; row: TdeeResultRow }
  | { kind: 'plan'; at: number; row: PlanRow }
  // CTA replay slot: reconstructed (not persisted), anchored at cta_shown_at
  // (stamped at result_payload time — for US sessions that equals the card's
  // real mount position; intl sessions may have seen it later, post-plan).
  | { kind: 'cta'; at: number; row: TdeeResultRow }

// Merge the three sources into one chronological stream. Stable sort: when a
// result and the assistant ack message land at the same timestamp (the calc
// row and the auto-emitted "Your TDEE breakdown is in the card above" text
// both come from the same request), the card sorts before the message so the
// user-facing rendering order is preserved.
function buildTimeline(
  messages: MessageRow[],
  results: TdeeResultRow[],
  plans: PlanRow[],
  cta: { at: number; row: TdeeResultRow } | null
): TimelineItem[] {
  const items: TimelineItem[] = [
    ...messages.map((m) => ({ kind: 'message' as const, at: m.created_at, msg: m })),
    ...results.map((r) => ({ kind: 'result' as const, at: r.created_at, row: r })),
    ...plans.map((p) => ({ kind: 'plan' as const, at: p.generated_at, row: p })),
  ]
  if (cta) items.push({ kind: 'cta', at: cta.at, row: cta.row })
  // cta ranks LAST at equal timestamps: cta_shown_at is stamped with the calc
  // row, but in the real UI the card mounts after the streamed bridge text.
  const rank: Record<TimelineItem['kind'], number> = { result: 0, message: 1, plan: 2, cta: 3 }
  return items.sort((a, b) => a.at - b.at || rank[a.kind] - rank[b.kind])
}

// Gmail-style thread render: each email is a card; sent emails get a pink
// accent stripe (NanoRhino brand), received emails get a neutral one. The
// subject only shows at the top of the thread (Gmail collapses repeated
// "Re: ..." subjects on each row). Markdown bodies render rich; received
// emails are plaintext so we preserve whitespace.
function EmailThread({ entries }: { entries: EmailThreadEntry[] }) {
  const threadSubject = entries[0]?.subject ?? null
  return (
    <div className="email-thread">
      {threadSubject && (
        <div className="email-thread-subject" title={threadSubject}>
          {threadSubject}
        </div>
      )}
      {entries.map((e, i) => {
        const isSent = e.direction === 'sent'
        const displayName = isSent ? 'NanoRhino Coach' : e.from
        const initials = isSent ? 'NR' : initialsFor(e.from)
        return (
          <article key={i} className={`email-row email-${e.direction}`}>
            <header className="email-row-head">
              <div className={`email-avatar ${isSent ? 'email-avatar-sent' : 'email-avatar-received'}`}>
                {initials}
              </div>
              <div className="email-row-meta">
                <div className="email-row-from">
                  <span className="email-row-name">{displayName}</span>
                  {isSent && e.kind === 'reply' && <span className="email-row-tag">reply</span>}
                  {isSent && e.kind === 'initial_plan' && (
                    <span className="email-row-tag email-row-tag-primary">initial plan</span>
                  )}
                  {e.error && <span className="email-row-tag email-row-tag-error">error</span>}
                </div>
                <div className="email-row-addrs muted small">
                  {isSent ? 'to' : 'from'} {isSent ? e.to : e.from}
                </div>
              </div>
              <div className="email-row-time muted small tabular">{fmtTime(e.at)}</div>
            </header>
            <div className="email-row-body">
              {isSent ? (
                <ReactMarkdown>{e.body}</ReactMarkdown>
              ) : (
                <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{e.body}</p>
              )}
            </div>
          </article>
        )
      })}
    </div>
  )
}

function initialsFor(addr: string): string {
  const local = addr.split('@')[0] ?? ''
  // Strip plus-addressing
  const base = local.split('+')[0]
  const parts = base.split(/[._-]/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return (base.slice(0, 2) || '?').toUpperCase()
}

// ---- Behavior timeline -------------------------------------------------------
// The transcript above shows what the USER saw; this panel shows what the
// SYSTEM did and what the user DID (clicks, captures, deliveries). Keeping
// the two apart is the whole point: a plan generated for an email must never
// read as a plan streamed in chat (the dfb4ec72 false alarm, 2026-06-12).

interface BehaviorEvent {
  at: number
  label: string
  tag?: { text: string; cls?: 'primary' | 'error' }
  // Expandable markdown body (used for email-deliverable plans so their
  // content stays inspectable without being mixed into the transcript).
  body?: string
}

function buildBehaviorEvents(
  c: ConversationRow,
  results: TdeeResultRow[],
  plans: PlanRow[],
  emails: EmailThreadEntry[]
): BehaviorEvent[] {
  const ev: BehaviorEvent[] = [{ at: c.created_at, label: 'Session started' }]
  for (const r of results) {
    ev.push({ at: r.created_at, label: `TDEE result computed — rec ${r.tdee_recommended} kcal` })
  }
  if (c.cta_shown_at != null) {
    // markCtaShown stamps when result_payload fires (chat.ts) — the CTA card
    // itself renders client-side moments later (US: right under the calc
    // card; intl: after the in-chat plan).
    ev.push({
      at: c.cta_shown_at,
      label:
        c.country === 'US'
          ? 'SMS claim CTA shown (stamped at result delivery)'
          : 'Plan CTA shown (stamped at result delivery)',
    })
  }
  for (const p of plans) {
    const what = p.kind === 'plan' ? 'Plan' : 'Sample meal day'
    if (isBackfillPlan(p)) {
      ev.push({
        at: p.generated_at,
        label: `${what} generated as email deliverable${p.model_provider ? ` · ${providerLabel(p.model_provider)}` : ''}`,
        tag: { text: 'never shown in chat' },
        body: p.markdown_body,
      })
    } else {
      ev.push({
        at: p.generated_at,
        label: `${what} streamed in chat${p.model_provider ? ` · ${p.model_provider}` : ''}`,
        tag: { text: 'in transcript above', cls: 'primary' },
      })
    }
  }
  if (c.wa_link_clicked_at != null) {
    ev.push({
      at: c.wa_link_clicked_at,
      label: 'WhatsApp link clicked (beacon)',
      tag: { text: 'conversion', cls: 'primary' },
    })
  }
  if (c.sms_link_clicked_at != null) {
    ev.push({
      at: c.sms_link_clicked_at,
      label: 'SMS link clicked (beacon)',
      tag: { text: 'conversion', cls: 'primary' },
    })
  }
  for (const e of emails) {
    if (e.direction === 'sent') {
      ev.push({
        at: e.at,
        label: e.kind === 'initial_plan' ? 'Initial-plan email sent' : 'Coach reply email sent',
        tag: e.error ? { text: 'send error', cls: 'error' } : { text: 'sent', cls: 'primary' },
      })
    } else {
      ev.push({ at: e.at, label: 'Email reply received from user' })
    }
  }
  if (c.share_copied_at != null) ev.push({ at: c.share_copied_at, label: 'Share link copied' })
  if (c.unsubscribed_at != null) {
    ev.push({ at: c.unsubscribed_at, label: 'Unsubscribed from coach emails', tag: { text: 'unsub', cls: 'error' } })
  }
  return ev.sort((a, b) => a.at - b.at)
}

function captureSourceLabel(source: string | null): string {
  if (source === 'us_fallback') return 'US fallback form (under the SMS CTA)'
  if (source === 'intl_form') return 'intl post-plan form'
  return source ? `source: ${source}` : 'source unknown (legacy capture)'
}

function StatusChip({ label, value, tone }: { label: string; value: string; tone?: 'on' | 'off' | 'warn' }) {
  return (
    <div className="status-chip">
      <span className="status-chip-label">{label}</span>
      <span className={`status-chip-value${tone ? ` status-${tone}` : ''}`}>{value}</span>
    </div>
  )
}

function BehaviorTimeline({
  c,
  results,
  plans,
  emails,
}: {
  c: ConversationRow
  results: TdeeResultRow[]
  plans: PlanRow[]
  emails: EmailThreadEntry[]
}) {
  const events = buildBehaviorEvents(c, results, plans, emails)
  const chatPlanCount = plans.filter((p) => p.kind === 'plan' && !isBackfillPlan(p)).length
  const emailPlanCount = plans.filter((p) => p.kind === 'plan' && isBackfillPlan(p)).length
  const initialEmailOk = emails.some((e) => e.direction === 'sent' && e.kind === 'initial_plan' && !e.error)

  // At-a-glance answer to "how did (or didn't) the plan reach this user?".
  const planDelivery =
    chatPlanCount > 0
      ? 'streamed in chat'
      : emailPlanCount > 0
        ? initialEmailOk
          ? 'by email only — never in chat'
          : 'generated for email — send not confirmed'
        : 'no plan generated'

  // SMS click status is only a meaningful funnel signal where the SMS CTA
  // exists (US sessions with a minted token).
  const smsStatus =
    c.sms_link_clicked_at != null
      ? { value: `clicked ${fmtTime(c.sms_link_clicked_at)}`, tone: 'on' as const }
      : c.country === 'US' && c.cta_shown_at != null
        ? { value: 'never clicked', tone: 'warn' as const }
        : { value: c.country === 'US' ? 'n/a — CTA never shown' : 'n/a — non-US (email CTA)', tone: 'off' as const }

  // WhatsApp click status (2026-07-22 primary US CTA; also the intl surface
  // when a number + token exist). Shown whenever it was tapped, or for any US
  // session that had a CTA rendered (the WhatsApp-primary two-button card).
  // Tone stays neutral (off) when unclicked rather than warn, because a
  // pre-switch session had cta_shown_at without a WhatsApp button — don't flag
  // those as a WhatsApp drop-off.
  const waClicked = c.wa_link_clicked_at != null
  const showWaStatus = waClicked || (c.country === 'US' && c.cta_shown_at != null)
  const waStatus = waClicked
    ? { value: `clicked ${fmtTime(c.wa_link_clicked_at!)}`, tone: 'on' as const }
    : { value: 'not clicked', tone: 'off' as const }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Behavior & system events ({events.length})</h2>
      </div>

      <div className="status-strip">
        <StatusChip label="Plan delivery" value={planDelivery} tone={chatPlanCount + emailPlanCount > 0 ? 'on' : 'off'} />
        {showWaStatus && <StatusChip label="WhatsApp link" value={waStatus.value} tone={waStatus.tone} />}
        <StatusChip label="SMS link" value={smsStatus.value} tone={smsStatus.tone} />
        <StatusChip
          label="Email"
          value={c.email_captured ? `captured · ${captureSourceLabel(c.email_capture_source)}` : 'not captured'}
          tone={c.email_captured ? 'on' : 'off'}
        />
        <StatusChip
          label="Handoff token"
          value={c.handoff_token ?? 'never minted (no calc)'}
          tone={c.handoff_token ? 'on' : 'off'}
        />
        {c.share_view_count > 0 && <StatusChip label="Share views" value={String(c.share_view_count)} tone="on" />}
      </div>
      {c.handoff_token && (
        <div className="muted small status-note">
          SMS-coach claim of this token is not recorded in D1 — verify against the EC2 gateway logs.
        </div>
      )}

      <div className="event-track">
        {events.map((e, i) => (
          <div key={i} className="event-row">
            <div className="event-time muted small tabular">{fmtTime(e.at)}</div>
            <div className="event-main">
              <span className="event-label">{e.label}</span>
              {e.tag && (
                <span
                  className={`email-row-tag${e.tag.cls === 'primary' ? ' email-row-tag-primary' : ''}${e.tag.cls === 'error' ? ' email-row-tag-error' : ''}`}
                >
                  {e.tag.text}
                </span>
              )}
              {e.body && (
                <details className="event-body">
                  <summary className="muted small">view generated markdown</summary>
                  <div className="msg-body plan-body">
                    <ReactMarkdown>{e.body}</ReactMarkdown>
                  </div>
                </details>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
